import FileExplorer from '@/components/FileExplorer/FileExplorer';
import {
  fetchMarketingAssets,
  fetchMarketingAssetsByFolder,
  fetchMarketingAssetsFolderStructure,
} from '@/lib/api';
import { useDownload } from '@/hooks/useDownload';
import { styled } from '@mui/material/styles';
import { useCallback, useMemo, useRef, useState } from 'react';
import { FilterSchema } from '@/components/ResourcesSidebar/ResourcesSidebarHelpers';
import { MarketingAssetSchema } from '@/lib/types';
import { useFileExplorerData } from '@/hooks/useFileExplorerData';
import { assetQueryKeys } from '@/lib/assetQueryKeys';
import { Mode } from '@/components/FileExplorer/FileExplorerHelpers';
import { useBasketContext } from '@/contexts/BasketContext';
import BasketBar from '@/components/ShareBasketModal/BasketBar';
import { useFixedBasketBarClearance } from '@/lib/bottomOverlays';
import ShareBasketModal from '@/components/ShareBasketModal/ShareBasketModal';

const FiltersInitialState = {
  fileTypeMarketing: [],
  fileFormatMarketing: [],
};

const GameMarketingAssets = ({
  game_id,
  game_group,
  game_name,
}: {
  game_id: number;
  game_group?: string | null;
  game_name?: string;
}) => {
  const { idsFor, addMany, removeMany, count } =
    useBasketContext();
  const barClearance = useFixedBasketBarClearance();
  const [shareOpen, setShareOpen] = useState(false);
  const selected = useMemo(() => idsFor('marketing_assets'), [idsFor]);

  // FileExplorer hands back the full next selection for this
  // category; reconcile it against the basket rather than replacing
  // the basket, which also holds the other category's picks.
  const handleSelect = useCallback(
    (next: number[]) => {
      const nextSet = new Set(next);
      addMany('marketing_assets', next);
      removeMany(
        'marketing_assets',
        idsFor('marketing_assets').filter((id) => !nextSet.has(id)),
      );
    },
    [addMany, removeMany, idsFor],
  );
  const [path, setPath] = useState<string[]>([]);
  const [currentView, setCurrentView] = useState<Mode>('grid');
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [filters, setFilters] = useState<Partial<FilterSchema>>(
    FiltersInitialState,
  );

  const {
    folderStructure,
    filesData,
    filesFlatData,
    loading,
    loadingNextPage,
    totalCount,
  } = useFileExplorerData({
    filters,
    path,
    gameIds: [game_id],
    gameGroup: game_group,
    scrollContainerRef,
    folderStructureFn: fetchMarketingAssetsFolderStructure,
    folderStructureQueryKey:
      assetQueryKeys.marketing_assets.folderStructure,
    fetchFilesByFolderFn: fetchMarketingAssetsByFolder,
    fetchFilesByFolderQueryKey:
      assetQueryKeys.marketing_assets.byFolder,
    fetchFilesFlatFn: fetchMarketingAssets,
    fetchFilesFlatQueryKey: assetQueryKeys.marketing_assets.flat,
  });

  // The basket spans sections and games; this tab's Download must only act
  // on rows actually on screen, so scope it to what this tab loaded.
  const loadedIds = useMemo(
    () =>
      [
        ...((filesData as { id: number }[] | undefined) ?? []),
        ...((filesFlatData as { id: number }[] | undefined) ?? []),
      ].map((file) => file.id),
    [filesData, filesFlatData],
  );

  const viewSelected = useMemo(() => {
    const onScreen = new Set(
      [
        ...((filesData as MarketingAssetSchema[] | undefined) ?? []),
        ...((filesFlatData as MarketingAssetSchema[] | undefined) ?? []),
      ].map((file) => file.id),
    );
    return selected.filter((id) => onScreen.has(id));
  }, [filesData, filesFlatData, selected]);

  const { download, loading: downloadLoading } = useDownload();

  const handleDownloadClick = () => {
    if (!viewSelected.length) return;

    const downloadParams: {
      category: 'marketing_assets';
      file_ids: number[];
      extension?: string;
      entity_name?: string;
      game_name?: string;
    } = {
      category: 'marketing_assets',
      file_ids: viewSelected,
      game_name,
    };

    if (viewSelected.length === 1) {
      const allFiles = [
        ...(filesData as MarketingAssetSchema[]),
        ...(filesFlatData as MarketingAssetSchema[]),
      ];
      const selectedFile = allFiles.find(
        (file) => file.id === viewSelected[0],
      );
      if (selectedFile?.extension) {
        downloadParams.extension = selectedFile.extension;
      }
      if (selectedFile?.name) {
        downloadParams.entity_name = selectedFile.name;
      }
    }

    download(downloadParams);
  };

  return (
    <Root
      ref={scrollContainerRef}
      $barVisible={count > 0}
      $barClearance={barClearance}
    >
      <FileExplorer
        folderStructure={folderStructure}
        files={filesData as MarketingAssetSchema[]}
        filesFlatList={filesFlatData as MarketingAssetSchema[]}
        loading={loading}
        selected={selected}
        onSelect={handleSelect}
        selectedCount={viewSelected.length}
        showSelectedLabel={false}
        onDownloadClick={handleDownloadClick}
        type="marketing"
        showFilters
        filters={filters}
        onFiltersChange={setFilters}
        path={path}
        onPathChange={setPath}
        currentView={currentView}
        onViewChange={setCurrentView}
        hiddenRoot="marketing_assets"
        downloadLoading={downloadLoading}
        loadingNextPage={loadingNextPage}
      />
      <BasketBar
        variant="fixed"
        category="marketing_assets"
        loadedIds={loadedIds}
        totalCount={totalCount}
        onShareClick={() => setShareOpen(true)}
      />
      <ShareBasketModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
      />
    </Root>
  );
};

const Root = styled('div', {
  shouldForwardProp: (prop) =>
    prop !== '$barVisible' && prop !== '$barClearance',
})<{ $barVisible: boolean; $barClearance: number }>(
  ({ $barVisible, $barClearance }) => ({
    padding: 24,
    // Reserve room so the fixed bar never covers the last row or the
    // infinite-scroll trigger.
    paddingBottom: $barVisible ? $barClearance : 24,
  }),
);

export default GameMarketingAssets;
